// SPA page-view tracking for GA4 (ADR 0224). react-router v6 is a client router,
// so there are no full-page loads to fire GA's automatic page_view — we send one
// ourselves on every route change. Because gtag is configured with
// `send_page_view:false` (see ga.ts), this hook's initial fire is the ONLY
// page_view for the first route: exactly one per route, no load-time duplicate.
//
// Mounted once, inside the Router (App's AppInner). A no-op when GA is unset.

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { gaPageView } from "./ga";

/**
 * The GA4 `page_path` for a location: pathname + search (the hash is dropped — a
 * GA page_view keys on path, and the hash carries no routing here).
 */
export function pageViewPath(location: { pathname: string; search?: string }): string {
  return location.pathname + (location.search ?? "");
}

/**
 * Fire a GA4 page_view on mount (initial load) and on each client navigation.
 * Dedupes on the exact path so a re-render — or React StrictMode's dev double-
 * mount — never double-counts the same route.
 */
export function useGaPageViews(): void {
  const location = useLocation();
  const lastPathRef = useRef<string | null>(null);
  const path = pageViewPath(location);
  useEffect(() => {
    if (lastPathRef.current === path) return;
    lastPathRef.current = path;
    gaPageView(path);
  }, [path]);
}
